import {
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { from, lastValueFrom, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { isLargeScreen } from '../global.variable';
import { AppService } from './app.service';
import { DataCheckService } from './shared/services/data-check.service';
import { DeviceInfoService } from './shared/services/device-info.service';
import { Capacitor } from '@capacitor/core';
import { environment } from '../environments/environment';
@Injectable({
  providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {
  isPrimeContractorUser: boolean =
    this.dataCheckService.isPrimeContractorUser();
  isSuperUser: boolean = this.dataCheckService.isSuperUser();
  isAppWorkingFullyBool: boolean = true;
  isLargeScreen = isLargeScreen;
  isNativeApp = Capacitor.isNativePlatform();
  constructor(
    private appService: AppService,
    private router: Router,
    private dataCheckService: DataCheckService,
    private deviceInfoService: DeviceInfoService,
  ) {
    this.deviceInfoService.deviceInfoSubject.subscribe((value: any) => {
      if ('appStatus' in value) {
        this.isAppWorkingFullyBool = value?.appStatus;
      }
    });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    // convert promise to observable using 'from' operator
    return from(this.handle(req, next)).pipe(
      tap((event: any) => {
        if (
          // case wehere the server was returning > 500 errors or event oflfine mode and then suddenly returns < 500 implying server communication
          event instanceof HttpResponse &&
          event.status >= 200 &&
          event?.status < 500 &&
          !this.isAppWorkingFullyBool &&
          navigator.onLine &&
          !event?.body?.serviceWorkerResponse
        ) {
          this.deviceInfoService.deviceInfoSubject.next({ appStatus: true });
        }
      }),
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          if (error.status === 401) {
            // the token is invalid; clear the localstorage so that 'LoadGuard' wont redirect back into the app.
            const user = this.appService.getUserData();
            const gtAppTheme: any = this.appService.getGTAppTheme();
            const isIOSFirstTimeUser =
              localStorage.getItem(btoa('isIOSFirstTimeUser')) || 'false';

            const uniqueDeviceID = localStorage.getItem(btoa('uniqueDeviceID'));

            localStorage.clear();

            if (user?.token_expiry) {
              localStorage.setItem('email', user?.profile?.email);
            }
            this.appService.setGTAppTheme(gtAppTheme);
            localStorage.setItem(
              btoa('isIOSFirstTimeUser'),
              isIOSFirstTimeUser,
            );
            if (uniqueDeviceID)
              localStorage.setItem(btoa('uniqueDeviceID'), uniqueDeviceID);
            this.router.navigate(['/login']);
          }
          if (error.status === 404) {
            this.router.navigate(['/404']);
          }
          if (error.status >= 500) {
            if (this.appService.getToken()) {
              if (this.isLargeScreen) {
                this.router.navigate(['/503']);
              } else {
                this.deviceInfoService.deviceInfoSubject.next({
                  appStatus: false,
                });
              }
            } else {
              this.router.navigate(['/503']);
            }
          }
          if (error.status === 403) {
            if (this.isSuperUser === true) {
              this.router.navigate(['gtadmin/dashboard']);
            } else if (this.isPrimeContractorUser) {
              this.router.navigate(['pc']);
            } else {
              this.router.navigate(['/']);
            }
          }
          // Payment Failed Calls
          if (error.status === 402) {
            if (this.isSuperUser) {
              this.router.navigate(['/gtadmin/dashboard']);
            } else if (this.dataCheckService?.isSubscriberAdmin()) {
              this.router.navigate([
                '/subscription',
                {
                  state: error.error.message,
                },
              ]);
              this.deviceInfoService.deviceInfoSubject.next({
                subscriptionCheck: true,
              });
            } else {
              if (this.isPrimeContractorUser) {
                this.router.navigate(['/login']);
                localStorage.clear();
                sessionStorage.clear();
              } else {
                this.router.navigate(['/']).then(() => {
                  window.location.reload();
                });
              }
            }
          }
        }
        return throwError(() => error);
      }),
    );
  }
  async handle(req: HttpRequest<any>, next: HttpHandler) {
    if (
      this.isNativeApp &&
      'nativeAppUrl' in environment &&
      environment.nativeAppUrl
    ) {
      const reqUrl = req.url?.startsWith('/') ? req.url : `/${req.url}`;
      req = req.clone({
        url: `https://${environment.nativeAppUrl}${reqUrl}`,
      });
    }

    const token = this.appService.getToken();

    if (token) {
      req = await this.addToken(req, token);
    }

    try {
      return await lastValueFrom(next.handle(req));
    } catch (e) {
      console.error('HTTP Error:', e);
      throw e;
    }
  }

  private async addToken(
    request: HttpRequest<any>,
    token: string,
  ): Promise<HttpRequest<any>> {
    const apiResponse: any =
      await this.deviceInfoService.getApiHeadersAndExtras(token);
    const headerDict = apiResponse?.headerDict;
    const reqBody = apiResponse?.reqBody;
    let reqDict: any = {
      setHeaders: headerDict,
      body: {
        ...request.body,
        ...(reqBody ? reqBody : {}),
      },
    };

    return request.clone(reqDict);
  }
}
